import { __assign } from "tslib";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect } from "react";
import clsx from "clsx";
import styles from "./styles.css.js";
import InternalIcon from "../../icon/internal";
import ItemsList from "../items-list";
import MobileExpandableGroup from "../mobile-expandable-group/mobile-expandable-group";
import Tooltip from "../tooltip.js";
import useHiddenDescription from "../utils/use-hidden-description.js";
import { getMenuItemProps } from "../utils/menu-item.js";
var MobileExpandableCategoryElement = function(_a) {
    var _b, _c, _d;
    var item = _a.item, onItemActivate = _a.onItemActivate, onGroupToggle = _a.onGroupToggle, targetItem = _a.targetItem, isHighlighted = _a.isHighlighted, isKeyboardHighlight = _a.isKeyboardHighlight, isExpanded = _a.isExpanded, highlightItem = _a.highlightItem, disabled = _a.disabled, variant = _a.variant;
    var highlighted = isHighlighted(item);
    var expanded = isExpanded(item);
    var isKeyboardHighlighted = isKeyboardHighlight(item);
    var triggerRef = React.useRef(null);
    useEffect(function() {
        if (triggerRef.current && highlighted && !expanded) {
            triggerRef.current.focus();
        }
    }, [
        expanded,
        highlighted
    ]);
    var onClick = function(e) {
        if (!disabled) {
            e.preventDefault();
            onGroupToggle(item, e);
        }
    };
    var onHover = function(event) {
        event.preventDefault();
        highlightItem(item);
    };
    var isDisabledWithReason = !!item.disabledReason && item.disabled;
    var _e = useHiddenDescription(item.disabledReason), targetProps = _e.targetProps, descriptionEl = _e.descriptionEl;
    var trigger = item.text && /*#__PURE__*/ React.createElement("span", __assign({
        className: clsx(styles.header, styles["expandable-header"], styles["variant-".concat(variant)], (_b = {}, _b[styles.highlighted] = highlighted, _b[styles["rolled-down"]] = expanded, _b[styles.disabled] = disabled, _b[styles["is-focused"]] = isKeyboardHighlighted, _b)),
        // We are using the roving tabindex technique to manage the focus state of the dropdown.
        // The current element will always have tabindex=0 which means that it can be tabbed to,
        // while all other items have tabindex=-1 so we can focus them when necessary.
        tabIndex: highlighted ? 0 : -1,
        ref: triggerRef
    }, getMenuItemProps({
        parent: true,
        disabled: disabled,
        expanded: expanded
    }), isDisabledWithReason ? targetProps : {}), item.text, /*#__PURE__*/ React.createElement("span", {
        className: clsx(styles["expand-icon"], (_c = {}, _c[styles["expand-icon-up"]] = expanded, _c))
    }, /*#__PURE__*/ React.createElement(InternalIcon, {
        name: "caret-down-filled"
    })));
    var content;
    if (isDisabledWithReason) {
        content = /*#__PURE__*/ React.createElement(React.Fragment, null, descriptionEl, /*#__PURE__*/ React.createElement(Tooltip, {
            content: item.disabledReason
        }, trigger));
    } else if (disabled) {
        content = trigger;
    } else {
        content = /*#__PURE__*/ React.createElement(MobileExpandableGroup, {
            open: expanded,
            trigger: trigger
        }, item.items && expanded && /*#__PURE__*/ React.createElement("ul", {
            role: "menu",
            "aria-label": item.text,
            className: clsx(styles["items-list-container"])
        }, /*#__PURE__*/ React.createElement(ItemsList, {
            items: item.items,
            onItemActivate: onItemActivate,
            onGroupToggle: onGroupToggle,
            targetItem: targetItem,
            isHighlighted: isHighlighted,
            isKeyboardHighlight: isKeyboardHighlight,
            isExpanded: isExpanded,
            highlightItem: highlightItem,
            hasCategoryHeader: true,
            variant: variant
        })));
    }
    return /*#__PURE__*/ React.createElement("li", {
        className: clsx(styles.category, styles["variant-".concat(variant)], styles.expandable, (_d = {}, _d[styles.expanded] = expanded, _d[styles.disabled] = disabled, _d[styles.highlighted] = highlighted || expanded, _d[styles.expandable] = true, _d)),
        role: "presentation",
        onClick: onClick,
        onMouseEnter: onHover,
        onTouchStart: onHover,
        "data-testid": item.id
    }, content);
};
export default MobileExpandableCategoryElement; //# sourceMappingURL=mobile-expandable-category-element.js.map
